/*
 * Copyright © 2015-2019 Serenova, LLC. All rights reserved.
 */

import 'rxjs/add/operator/map';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/mergeMap';

import { fromPromise } from 'rxjs/observable/fromPromise';
import { from } from 'rxjs/observable/from';
import { Toast } from 'cx-ui-components';
import { sdkPromise } from '../../../../utils/sdk';
import { handleError, handleProgressSuccess, handleSuccess, handleSuccessSyncUser } from '../handleResult';
import {
  getCurrentEntity,
  getMsteamsSyncUserData,
  getMsteamsSyncUserDataError,
  getSelectedEntity,
  getSelectedEntityId,
  getSelectedSubEntityId,
  userHasPermissions,
} from '../selectors';
import { entitiesMetaData } from '../metaData';
import { camelCaseToKebabCase, camelCaseToRegularFormAndRemoveLastLetter } from 'serenova-js-utils/strings';
import {
  getMsalInstance,
  prepareEmailListenerProperties,
  prepareEmailProperties,
  prepareIntegrationProperties
} from './utilities';
import { fromJS } from 'immutable';
import { selectIntegrationListenerFormInitialValues } from "./selectors";
import { isFormPristine } from '../../form/selectors';

export const FetchData = (action$, store) =>
  action$
    .ofType('FETCH_DATA')
    .filter(({ entityName }) => entityName === 'integrations')
    .mergeMap((a) =>
      fromPromise(sdkPromise(entitiesMetaData[a.entityName].entityApiRequest('get', 'mainEntity')))
        .map((response) => {
          const msTeamsReadUpdatePermission = userHasPermissions(store.getState(), ['MSTEAMS_INTEGRATIONS_READ', 'MSTEAMS_INTEGRATIONS_UPDATE'])
          return handleSuccess(
            {
              result: msTeamsReadUpdatePermission ? response.result.filter(
                (integration) => integration.type !== 'plivo' && integration.type !== 'birst'
              ) : response.result.filter(
                (integration) => integration.type !== 'plivo' && integration.type !== 'birst' && integration.type !== 'ms-teams'
              ),
            },
            a
          )
        })
        .catch((error) => handleError(error, a))
    );

export const IntegrationActiveValidation = (action$, store) =>
  action$
    .ofType('FORM_SUBMIT')
    .map(a => ({
      ...a,
      entityName: getCurrentEntity(store.getState()),
      selectedEntityId: getSelectedEntityId(store.getState()),
      selectedEntity: getSelectedEntity(store.getState())?.toJS()
    }))
    .filter(({ dirty, entityName }) => dirty && entityName === 'integrations')
    .map(a => {
      if (a.values.get('type') === 'email' ) {
        if (!a.selectedEntity.active) {
          return {
            type: 'UPDATE_ENTITY',
            entityName: a.entityName,
            entityId: a.selectedEntityId,
            values: a.values.toJS(),
            selectedEntity: a.selectedEntity
          };
        } else {
          Toast.error('Please disable the integration before continue.');
          return {
            type: 'UPDATE_ENTITY_REJECTED',
            entityName: a.entityName,
            entityId: a.selectedEntityId
          };
        }
      } else if (a.values.get('type') === 'salesforce-service-cloud' &&
        a.selectedEntityId !== 'create' &&
        a.selectedEntityId !== 'bulk' &&
        a.selectedEntity !== undefined && a.selectedEntity.active == true) {
        Toast.error('Please disable the integration before continue.');
        return {
          type: 'UPDATE_ENTITY_REJECTED',
          entityName: a.entityName,
          entityId: a.selectedEntityId
        };
      } else {
        if (a.selectedEntityId === 'create') {
          return {
            type: 'CREATE_ENTITY',
            entityName: a.entityName,
            entityId: a.selectedEntityId,
            values: a.values.toJS()
          };
        } else if (a.selectedEntityId === 'bulk') {
          return {
            type: 'BULK_ENTITY_UPDATE',
            entityName: a.entityName,
            values: a.values.toJS()
          };
        } else {
          return {
            type: 'UPDATE_ENTITY',
            entityName: a.entityName,
            entityId: a.selectedEntityId,
            values: a.values.toJS()
          };
        }
      }
    });

export const CreateEntity = (action$) =>
  action$
    .ofType('CREATE_ENTITY')
    .filter(({ entityName }) => entityName === 'integrations')
    .map((a) => {
      a.sdkCall = entitiesMetaData[a.entityName].entityApiRequest('create', 'singleMainEntity');
      a.sdkCall.path = entitiesMetaData[a.entityName].sdkCall.path
        ? entitiesMetaData[a.entityName].sdkCall.path
        : [camelCaseToKebabCase(a.entityName.replace(/[V|v]\d{1}/, ''))];
      let properties = prepareIntegrationProperties(a.values.type, a.values.authType, a.values.properties,a);
      if (a.values.type === 'email') {
        properties = prepareEmailProperties(properties);
      }

      const isSalesforceV2 = a.values.type === 'salesforce' && properties.authVersion === 'v2-auth-code';
      const active = a.values.type !== 'ms-teams'
        && a.values.type !== "salesforce-service-cloud"
        && !isSalesforceV2;
      a.sdkCall.data = { ...a.values, active, 'integration-type': a.values.type, properties };
      a.sdkCall.apiVersion = entitiesMetaData[a.entityName].sdkCall.apiVersion;
      return { ...a };
    })
    .concatMap((a) =>
      fromPromise(sdkPromise(a.sdkCall))
        .map((response) =>
          handleSuccess(
            response,
            a,
            `${camelCaseToRegularFormAndRemoveLastLetter(a.entityName)} was created successfully!`
          )
        )
        .catch((error) => handleError(error, a))
    );

export const UpdateEntity = (action$, store) =>
  action$
    .ofType('UPDATE_ENTITY')
    .filter(({ entityName }) => entityName === 'integrations')
    .map((a) => {
      a.sdkCall = entitiesMetaData[a.entityName].entityApiRequest('update', 'singleMainEntity');
      a.sdkCall.path = [camelCaseToKebabCase(a.entityName), a.entityId];
      a.sdkCall.apiVersion = entitiesMetaData[a.entityName].sdkCall.apiVersion;
      let properties = prepareIntegrationProperties(a.values.type, a.values.authType, a.values.properties,a);
      if (a.values.type === 'email') {
        properties = prepareEmailProperties(properties);
      }

      delete properties.syncedAdUsers;
      delete properties.totalAdUsers;

      const { created, createdBy, updated, updatedBy, updating, bulkChangeItem, ...data } = a.values;
      a.sdkCall.data = {
        ...data,
        integrationId: a.entityId,
        properties,
      };
      return { ...a };
    })
    .concatMap((a) =>
      fromPromise(sdkPromise(a.sdkCall))
        .map((response) =>
          handleSuccess(
            response,
            a,
            `${camelCaseToRegularFormAndRemoveLastLetter(a.entityName)} was updated successfully!`
          )
        )
        .catch((error) => {
          return handleError(error, a);
        })
    );

export const CreateIntegrationListenerFormSubmission = (action$, store) =>
  action$
    .ofType('INTEGRATION_LISTENER_FORM_SUBMIT')
    .debounceTime(300)
    .filter(({ dirty }) => dirty)
    .map((a) => {
      a.selectedSubEntity = selectIntegrationListenerFormInitialValues(store.getState()).toJS();
      a.subEntityId = getSelectedSubEntityId(store.getState());
      a.entityId = getSelectedEntityId(store.getState());
      let properties;

      const selectedEntity = getSelectedEntity(store.getState()).toJS();
      try {
        a.values = a.values.toJS();
        properties = a.values.properties;
      } catch (e) {}
      if (selectedEntity && selectedEntity.type === 'email') {
        properties = prepareEmailListenerProperties(properties, selectedEntity.properties);
      }
      let data = a.values;
      data = {
        ...data,
        properties
      };
      a.sdkCall = {
        command: a.subEntityId === 'listeners' ? 'createIntegrationListener' : 'updateIntegrationListener',
        data: {
          ...data,
          integrationId: a.entityId,
          listenerId: a.subEntityId,
        },
        module: 'entities',
        topic: 'cxengage/entities/create-integration-listener-response',
      };
      a.selectedEntity = selectedEntity;
      return { ...a };
    })
    .concatMap((a) =>
      fromPromise(sdkPromise(a.sdkCall))
        .map((response) =>
          handleSuccess(
            response,
            a,
            a.subEntityId === 'listeners'
              ? `A new listener for this Integration was created successfully!`
              : `<i>Listener</i> was updated successfully!`
          )
        )
        .catch((error) => handleError(error, a))
    );

export const RemoveTwilioGlobalDialParamFormSubmission = (action$, store) =>
  action$
    .ofType('REMOVE_TWILIO_GLOBAL_DIAL_PARAM')
    .map((a) => {
      a.entityName = getCurrentEntity(store.getState());
      a.entityId = getSelectedEntity(store.getState()).get('id');
      a.values = getSelectedEntity(store.getState()).toJS();
      if (a.values.properties.globalDialParams.hasOwnProperty(a.subEntityName)) {
        delete a.values.properties.globalDialParams[a.subEntityName];
      }
      return { ...a };
    })
    .map((a) => ({
      ...a,
      type: 'UPDATE_ENTITY',
    }));

export const UpdateEntityFulfilled = (action$) =>
  action$
    .ofType('UPDATE_ENTITY_FULFILLED')
    .filter((a) => a.entityName === 'integrations' && a.values && a.values.type !== 'twilio' && a.values.type !== 'socialconnector')
    .map((a) => {
      const { authType, bulkChangeItem } = a.values;
      const result = { ...a.response.result, bulkChangeItem };
      const responseAuthType = result.authType || (result.properties && result.properties.authType);
      if (responseAuthType) {
        result.authType = responseAuthType;
      } else if (authType) {
        result.authType = authType;
      }
      delete result.active;
      // Preserve masked placeholders for rest integration after update so the
      // initialized form shows a masked field when the backend omits the secret.
      if (a.values && a.values.type === 'rest') {
        const props = result.properties || {};
        const restAuthType = result.authType || a.values.authType;
        if (restAuthType === 'basic') {
          const pwd = props.password;
          if (pwd === undefined || pwd === null || pwd === '') {
            props.password = '********';
          }
        } else if (restAuthType === 'token') {
          const t = props.token;
          if (t === undefined || t === null || t === '') {
            props.token = '********';
          }
        }
        result.properties = props;
      }
      return { ...a, result };
    })
    .map((a) => ({
      type: '@@redux-form/INITIALIZE',
      meta: {
        form: `${a.entityName}:${a.entityId}`,
        keepDirty: false,
        updateUnregisteredFields: false,
      },
      payload: { ...a.result },
    }));

export const UpdateSocialConnectorEntityFulfilled = (action$) =>
  action$
    .ofType('UPDATE_ENTITY_FULFILLED')
    .filter((a) => a.entityName === 'integrations' && a.values && a.values.type === 'socialconnector')
    .map((a) => {
      if (a?.response?.result?.properties) {
        a.response.result.socialConnectorProperties = Object.entries(a.response.result.properties)
          .map(([label, value]) => ({ label, value }));
      }
      const result = { ...a.response.result }
      return { ...a, result };
    })
    .map((a) => ({
      type: '@@redux-form/INITIALIZE',
      meta: {
        form: `${a.entityName}:${a.entityId}`,
        keepDirty: false,
        updateUnregisteredFields: false,
      },
      payload: { ...a.result },
    }));

export const UpdateWebRtcLabelInTenantSettings = (action$) =>
  action$
    .ofType('UPDATE_ENTITY_FULFILLED')
    .filter(({ entityName, values }) => entityName === 'integrations' && values && values.type === 'twilio')
    .concatMap((a) =>
      fromPromise(
        sdkPromise({
          crudAction: 'update',
          path: ['settings', 'web-rtc-extension-label'],
          command: 'updateTenantWebRtcLabel',
          module: 'entities',
          data: {
            value: a.values.webRtcLabel ? a.values.webRtcLabel : '',
          },
          topic: 'cxengage/entities/update-tenant-settings/web-rtc-label',
        })
      )
        .map((response) => handleSuccess(response, { ...a, type: 'UPDATE_WEB_RTC_LABEL' }))
        .catch((error) => {
          if (error && error.data.apiResponse.response.error.code === 404) {
            return fromPromise(
              sdkPromise({
                crudAction: 'create',
                path: ['settings'],
                command: 'updateTenantWebRtcLabel',
                module: 'entities',
                data: {
                  name: 'web-rtc-extension-label',
                  value: a.values.webRtcLabel ? a.values.webRtcLabel : '',
                },
                topic: 'cxengage/entities/update-tenant-settings/web-rtc-label',
              })
            )
              .map((response) => handleSuccess(response, { ...a, type: 'UPDATE_WEB_RTC_LABEL' }))
              .catch((error) =>
                handleError(error, { ...a, type: 'UPDATE_WEB_RTC_LABEL' }, 'Unable to update WebRTC value')
              );
          } else {
            return handleError(error, { ...a, type: 'UPDATE_WEB_RTC_LABEL' }, 'Unable to update WebRTC value');
          }
        })
    );

export const UpdateEcgWebRtcLabelInTenantSettings = (action$) =>
  action$
    .ofType('UPDATE_ENTITY_FULFILLED')
    .filter(({ entityName, values }) => entityName === 'integrations' && values && values.type === 'ecg')
    .concatMap((a) =>
      fromPromise(
        sdkPromise({
          crudAction: 'update',
          path: ['settings', 'ecg-web-rtc-extension-label'],
          command: 'updateTenantEcgWebRtcLabel',
          module: 'entities',
          data: {
            value: a.values.ecgWebRtcLabel ? a.values.ecgWebRtcLabel : '',
          },
          topic: 'cxengage/entities/update-tenant-settings/ecg-web-rtc-label',
        })
      )
        .map((response) => handleSuccess(response, { ...a, type: 'UPDATE_ECG_WEB_RTC_LABEL' }))
        .catch((error) => {
          const errorCode = error?.data?.apiResponse?.response?.error?.code;
          if (errorCode === 404) {
            return fromPromise(
              sdkPromise({
                crudAction: 'create',
                path: ['settings'],
                command: 'updateTenantEcgWebRtcLabel',
                module: 'entities',
                data: {
                  name: 'ecg-web-rtc-extension-label',
                  value: a.values.ecgWebRtcLabel ? a.values.ecgWebRtcLabel : '',
                },
                topic: 'cxengage/entities/update-tenant-settings/ecg-web-rtc-label',
              })
            )
              .map((response) => handleSuccess(response, { ...a, type: 'UPDATE_ECG_WEB_RTC_LABEL' }))
              .catch((error) =>
                handleError(error, { ...a, type: 'UPDATE_ECG_WEB_RTC_LABEL' }, 'Unable to update ECG WebRTC value')
              );
          } else {
            return handleError(error, { ...a, type: 'UPDATE_ECG_WEB_RTC_LABEL' }, 'Unable to update ECG WebRTC value');
          }
        })
    );

/* when oauth token for email integration is updated and smtp user matches listener email,
 * then update the listener automatically */
export const UpdateEmailIntegrationFulfilled = (action$) =>
  action$
    .ofType('UPDATE_ENTITY_FULFILLED')
    .filter(({ entityName, values }) => entityName === 'integrations' && values && values.type === 'email')
    .concatMap((a) => {
      delete a.sdkCall;
      let listeners;
      if (a.values && a.selectedEntity) {
        if (a.values.properties.oauthCredentialId !== a.selectedEntity.properties.oauthCredentialId) {
          listeners = a.selectedEntity.listeners.filter((item) =>
            item.properties.email === a.selectedEntity.properties.smtpUser
          );
        }
      }

      if (listeners) {
        listeners.forEach((listener) => {
          if (listener.properties.oauthCredential || listener.properties.oauthCredentialId) {
            let newListener = listener;
            newListener.properties.oauthCredential = a.values.properties.oauthCredential;
            newListener.properties.oauthCredentialId = a.values.properties.oauthCredentialId;
            newListener.properties = prepareEmailListenerProperties(newListener.properties, a.selectedEntity.properties);

            let data = newListener;
            a.sdkCall = {
              command: 'updateIntegrationListener',
              data: {
                ...data,
                integrationId: a.entityId,
                listenerId: listener.id,
              },
              module: 'entities',
              topic: 'cxengage/entities/create-integration-listener-response',
            };
            a.type = 'EMAIL_INTEGRATION_LISTENER_FORM_SUBMIT';
            a.subEntityId = newListener.id;

            fromPromise(sdkPromise(a.sdkCall));
          }
        });
      }

      // no need actual response to be returned
      return [];
    });

/* when oauth token is updated in the listener for email integration and smtp user matches with email,
 * then update the integration automatically */
export const UpdateEmailListenerFulfilled = (action$) =>
  action$
    .ofType('INTEGRATION_LISTENER_FORM_SUBMIT_FULFILLED')
    .filter(({ entityName, dirty, values, selectedEntity }) => entityName === 'integrations' && dirty && values && selectedEntity && selectedEntity.type === 'email')
    .concatMap((a) => {
      delete a.sdkCall;
      let values = a.values;
      if (values && a.selectedSubEntity && a.selectedEntity) {
        if (values.properties.email === a.selectedEntity.properties.smtpUser) {
          if (!a.selectedSubEntity.properties ||
            (values.properties.oauthCredentialId !== a.selectedSubEntity.properties.oauthCredentialId)) {
            let updatedEntity = a.selectedEntity;
            updatedEntity.properties.oauthCredential = values.properties.oauthCredential;
            updatedEntity.properties.oauthCredentialId = values.properties.oauthCredentialId;
            updatedEntity.properties = prepareEmailProperties(updatedEntity.properties);

            a.sdkCall = entitiesMetaData[a.entityName].entityApiRequest('update', 'singleMainEntity');
            a.sdkCall.path = [camelCaseToKebabCase(a.entityName), a.entityId];
            a.sdkCall.apiVersion = entitiesMetaData[a.entityName].sdkCall.apiVersion;

            a.sdkCall.data = {
              data: updatedEntity,
              integrationId: a.entityId,
              properties: updatedEntity.properties,
            };

            fromPromise(sdkPromise(a.sdkCall));
          }
        }
      }

      // no need actual response to be returned
      return [];
    });

export const ReIntIntegrationsFormAfterUpdatingWebRtc = (action$, store) =>
  action$
    .ofType('UPDATE_WEB_RTC_LABEL_FULFILLED')
    .map((a) => ({
      ...a,
      selectedEntity: getSelectedEntity(store.getState()) ? getSelectedEntity(store.getState()).toJS() : fromJS({}),
    }))
    .map((a) => {
      const payload = { ...a.selectedEntity, webRtcLabel: a.response.result.value };
      if (a.selectedEntity && a.selectedEntity.type === 'twilio') {
        const auth = a.selectedEntity.properties ? a.selectedEntity.properties.authToken : undefined;
        if (auth === undefined || auth === null || auth === '') {
          payload.properties = Object.assign({}, a.selectedEntity.properties || {}, { authToken: '********' });
        }
      }
      return {
        type: '@@redux-form/INITIALIZE',
        meta: {
          form: `${a.entityName}:${a.entityId}`,
          keepDirty: false,
          updateUnregisteredFields: false,
        },
        payload,
      };
    });

export const ReIntIntegrationsFormAfterUpdatingEcgWebRtc = (action$, store) =>
  action$
    .ofType('UPDATE_ECG_WEB_RTC_LABEL_FULFILLED')
    .map((a) => ({
      ...a,
      selectedEntity: getSelectedEntity(store.getState()) ? getSelectedEntity(store.getState()).toJS() : fromJS({}),
    }))
    .map((a) => ({
      type: '@@redux-form/INITIALIZE',
      meta: {
        form: `${a.entityName}:${a.entityId}`,
        keepDirty: false,
        updateUnregisteredFields: false,
      },
      payload: { ...a.selectedEntity, ecgWebRtcLabel: a.response.result.value },
    }));

export const SyncWebRtcLabelToIntegrationsForm = (action$, store) =>
  action$
    .ofType('FETCH_WEB_RTC_LABEL_FULFILLED', 'FETCH_ECG_WEB_RTC_LABEL_FULFILLED')
    .map((a) => ({
      ...a,
      selectedEntityId: getSelectedEntityId(store.getState()),
      formPristine: isFormPristine(store.getState()),
    }))
    .filter(({ id, selectedEntityId, formPristine }) => id === selectedEntityId && formPristine)
    .map((a) => {
      const selectedEntity = getSelectedEntity(store.getState()) ? getSelectedEntity(store.getState()).toJS() : {};
      const form = `${a.entityName}:${a.id}`;
      if (a.type === 'FETCH_WEB_RTC_LABEL_FULFILLED') {
        const payload = { ...selectedEntity, webRtcLabel: a.response.result.value };
        if (selectedEntity && selectedEntity.type === 'twilio') {
          const auth = selectedEntity.properties ? selectedEntity.properties.authToken : undefined;
          if (auth === undefined || auth === null || auth === '') {
            payload.properties = Object.assign({}, selectedEntity.properties || {}, { authToken: '********' });
          }
        }
        return {
          type: '@@redux-form/INITIALIZE',
          meta: {
            form,
            keepDirty: true,
            updateUnregisteredFields: false,
          },
          payload,
        };
      }
      return {
        type: '@@redux-form/INITIALIZE',
        meta: {
          form,
          keepDirty: true,
          updateUnregisteredFields: false,
        },
        payload: { ...selectedEntity, ecgWebRtcLabel: a.response.result.value },
      };
    });


export const FetchSyncProgressDetailsOn = (action$, store) =>
  action$
    .ofType('FETCH_DATA_ITEM_FULFILLED')
    .map((a) => ({
      ...a,
      selectedEntity: getSelectedEntity(store.getState()),
    }))
    .filter(
      ({ entityName, selectedEntity }) =>
        entityName === 'integrations' && selectedEntity && selectedEntity.get('type') === 'ms-teams'
    )
    .map((a) => {
        return { ...a, type: 'SYNC_USERS_ENTITY_PROGRESS' }
      }
    );

export const FetchWebRtcLabelSettings = (action$, store) =>
  action$
    .ofType('FETCH_DATA_ITEM_FULFILLED')
    .map((a) => ({
      ...a,
      selectedEntity: getSelectedEntity(store.getState()),
    }))
    .filter(
      ({ entityName, selectedEntity }) =>
        entityName === 'integrations' && selectedEntity && selectedEntity.get('type') === 'twilio'
    )
    .concatMap((a) =>
      fromPromise(
        sdkPromise({
          crudAction: 'read',
          path: ['settings', 'web-rtc-extension-label'],
          command: 'getTenantWebRtcLabel',
          module: 'entities',
          topic: 'cxengage/entities/get-tenant-settings/web-rtc-label',
        })
      )
        .map((response) => handleSuccess(response, { ...a, type: 'FETCH_WEB_RTC_LABEL' }))
        .catch((error) => handleError(error, { ...a, type: 'FETCH_WEB_RTC_LABEL' }, 'Unable to fetch WebRTC value'))
    );

export const InitializeTwilioAuthMaskOnFetch = (action$, store) =>
  action$
    .ofType('FETCH_DATA_ITEM_FULFILLED')
    .map((a) => ({
      ...a,
      selectedEntityId: getSelectedEntityId(store.getState()),
      formPristine: isFormPristine(store.getState()),
      selectedEntity: getSelectedEntity(store.getState()),
    }))
    .filter(({ id, selectedEntityId, formPristine, selectedEntity }) => id === selectedEntityId && formPristine && selectedEntity)
    .filter(
      ({ entityName, selectedEntity }) =>
        entityName === 'integrations' && selectedEntity && selectedEntity.get('type') === 'twilio'
    )
    .filter(({ selectedEntity }) => {
      const auth = selectedEntity && selectedEntity.getIn ? selectedEntity.getIn(['properties', 'authToken']) : undefined;
      return auth === undefined || auth === null || auth === '';
    })
    .map((a) => {
      const selectedEntity = getSelectedEntity(store.getState()) ? getSelectedEntity(store.getState()).toJS() : {};
      const form = `${a.entityName}:${a.id}`;
      const properties = Object.assign({}, selectedEntity.properties || {}, { authToken: '********' });
      return {
        type: '@@redux-form/INITIALIZE',
        meta: { form, keepDirty: true, updateUnregisteredFields: false },
        payload: { ...selectedEntity, properties },
      };
    });

export const InitializeRestAuthMaskOnFetch = (action$, store) =>
  action$
    .ofType('FETCH_DATA_ITEM_FULFILLED')
    .map((a) => ({
      ...a,
      selectedEntityId: getSelectedEntityId(store.getState()),
      formPristine: isFormPristine(store.getState()),
      selectedEntity: getSelectedEntity(store.getState()),
    }))
    .filter(({ id, selectedEntityId, formPristine, selectedEntity }) => id === selectedEntityId && formPristine && selectedEntity)
    .filter(({ entityName, selectedEntity }) => entityName === 'integrations' && selectedEntity && selectedEntity.get('type') === 'rest')
    .filter(({ selectedEntity }) => {
      const authType = selectedEntity && selectedEntity.get
        ? (selectedEntity.get('authType') || selectedEntity.getIn(['properties', 'authType']))
        : undefined;
      const token = selectedEntity && selectedEntity.getIn ? selectedEntity.getIn(['properties', 'token']) : undefined;
      const password = selectedEntity && selectedEntity.getIn ? selectedEntity.getIn(['properties', 'password']) : undefined;
      const tokenMissing = token === undefined || token === null || token === '';
      const passwordMissing = password === undefined || password === null || password === '';
      if (authType === 'token') {
        return tokenMissing;
      }
      if (authType === 'basic') {
        return passwordMissing;
      }
      if (authType === 'noAuth') {
        return false;
      }
      return tokenMissing || passwordMissing;
    })
    .map((a) => {
      const selectedEntity = getSelectedEntity(store.getState()) ? getSelectedEntity(store.getState()).toJS() : {};
      const form = `${a.entityName}:${a.id}`;
      const props = Object.assign({}, selectedEntity.properties || {});
      const authType = selectedEntity.authType || (selectedEntity.properties && selectedEntity.properties.authType);
      const token = props.token;
      const password = props.password;
      if ((authType === 'token' || authType === undefined || authType === null || authType === '')
        && (token === undefined || token === null || token === '')) {
        props.token = '********';
      }
      if ((authType === 'basic' || authType === undefined || authType === null || authType === '')
        && (password === undefined || password === null || password === '')) {
        props.password = '********';
      }
      return {
        type: '@@redux-form/INITIALIZE',
        meta: { form, keepDirty: true, updateUnregisteredFields: false },
        payload: { ...selectedEntity, authType, properties: props },
      };
    });

export const FetchEcgWebRtcLabelSettings = (action$, store) =>
  action$
    .ofType('FETCH_DATA_ITEM_FULFILLED')
    .map((a) => ({
      ...a,
      selectedEntity: getSelectedEntity(store.getState()),
    }))
    .filter(
      ({ entityName, selectedEntity }) =>
        entityName === 'integrations' && selectedEntity && selectedEntity.get('type') === 'ecg'
    )
    .concatMap((a) =>
      fromPromise(
        sdkPromise({
          crudAction: 'read',
          path: ['settings', 'ecg-web-rtc-extension-label'],
          command: 'getTenantEcgWebRtcLabel',
          module: 'entities',
          topic: 'cxengage/entities/get-tenant-settings/ecg-web-rtc-label',
        })
      )
        .map((response) => handleSuccess(response, { ...a, type: 'FETCH_ECG_WEB_RTC_LABEL' }))
        .catch((error) => handleError(error, { ...a, type: 'FETCH_ECG_WEB_RTC_LABEL' }, 'Unable to fetch ECG WebRTC value'))
    );

/*
* temporary hiding fetch Google client id and secret
export const FetchGoogleOauthClientId = (action$, store) =>
  action$
    .ofType('FETCH_DATA_ITEM_FULFILLED')
    .map((a) => ({
      ...a,
      entityName: getCurrentEntity(store.getState()),
      selectedEntity: getSelectedEntity(store.getState()),
    }))
    .filter(
      ({ entityName, selectedEntity }) =>
        entityName === 'integrations' && selectedEntity && selectedEntity.get('type') === 'email'
    )
    .concatMap((a) =>
      fromPromise(
        sdkPromise({
          crudAction: 'read',
          path: ['settings', 'google-oauth-client-id'],
          command: 'getGoogleOauthClientId',
          module: 'entities',
          topic: 'cxengage/entities/get-tenant-settings/google-oauth-client-id',
        })
      )
        .map((response) => handleSuccess(response, { ...a, type: 'FETCH_GOOGLE_OAUTH_CLIENT_ID' }))
        .catch((error) => handleError(error, { ...a, type: 'FETCH_GOOGLE_OAUTH_CLIENT_ID' }, 'Unable to fetch Google Oauth Client Id value'))
    );

export const FetchGoogleOauthClientSecret = (action$, store) =>
  action$
    .ofType('FETCH_DATA_ITEM_FULFILLED')
    .map((a) => ({
      ...a,
      entityName: getCurrentEntity(store.getState()),
      selectedEntity: getSelectedEntity(store.getState()),
    }))
    .filter(
      ({ entityName, selectedEntity }) =>
        entityName === 'integrations' && selectedEntity && selectedEntity.get('type') === 'email'
    )
    .concatMap((a) =>
      fromPromise(
        sdkPromise({
          crudAction: 'read',
          path: ['settings', 'google-oauth-client-secret'],
          command: 'getGoogleOauthClientSecret',
          module: 'entities',
          topic: 'cxengage/entities/get-tenant-settings/google-oauth-client-secret',
        })
      )
        .map((response) => handleSuccess(response, { ...a, type: 'FETCH_GOOGLE_OAUTH_CLIENT_SECRET' }))
        .catch((error) => handleError(error, { ...a, type: 'FETCH_GOOGLE_OAUTH_CLIENT_SECRET' }, 'Unable to fetch Google Oauth Client Secret value'))
    );
*/

export const FetchMsOauthClientId = (action$, store) =>
  action$
    .ofType('FETCH_DATA_ITEM_FULFILLED')
    .map((a) => ({
      ...a,
      entityName: getCurrentEntity(store.getState()),
      selectedEntity: getSelectedEntity(store.getState()),
    }))
    .filter(
      ({ entityName, selectedEntity }) =>
        entityName === 'integrations' && selectedEntity && selectedEntity.get('type') === 'email'
    )
    .concatMap((a) =>
      fromPromise(
        sdkPromise({
          crudAction: 'read',
          path: ['settings', 'ms-oauth-client-id'],
          command: 'getMsOauthClientId',
          module: 'entities',
          topic: 'cxengage/entities/get-tenant-settings/ms-oauth-client-id',
        })
      )
        .map((response) => handleSuccess(response, { ...a, type: 'FETCH_MS_OAUTH_CLIENT_ID' }))
        .catch((error) => handleError(error, {
          ...a,
          type: 'FETCH_MS_OAUTH_CLIENT_ID'
        }, 'Unable to fetch Microsoft Oauth Client Id value'))
    );

export const FetchMsOauthClientSecret = (action$, store) =>
  action$
    .ofType('FETCH_DATA_ITEM_FULFILLED')
    .map((a) => ({
      ...a,
      entityName: getCurrentEntity(store.getState()),
      selectedEntity: getSelectedEntity(store.getState()),
    }))
    .filter(
      ({ entityName, selectedEntity }) =>
        entityName === 'integrations' && selectedEntity && selectedEntity.get('type') === 'email'
    )
    .concatMap((a) =>
      fromPromise(
        sdkPromise({
          crudAction: 'read',
          path: ['settings', 'ms-oauth-client-secret'],
          command: 'getMsOauthClientSecret',
          module: 'entities',
          topic: 'cxengage/entities/get-tenant-settings/ms-oauth-client-secret',
        })
      )
        .map((response) => handleSuccess(response, { ...a, type: 'FETCH_MS_OAUTH_CLIENT_SECRET' }))
        .catch((error) => handleError(error, {
          ...a,
          type: 'FETCH_MS_OAUTH_CLIENT_SECRET'
        }, 'Unable to fetch Microsoft Oauth Client Secret value'))
    );

export const FetchOauthAccessToken = (action$, store) =>
  action$
    .ofType('FETCH_OAUTH_ACCESS_TOKEN')
    .map((a) => ({
      ...a,
      entityName: getCurrentEntity(store.getState()),
      selectedEntity: getSelectedEntity(store.getState()),
      subEntityId: getSelectedSubEntityId(store.getState()),
    }))
    .filter(
      ({ entityName, selectedEntity }) =>
        entityName === 'integrations' && selectedEntity && selectedEntity.get('type') === 'email'
    )
    .concatMap((a) =>
      fromPromise(
        sdkPromise({
          crudAction: 'createwfm', //using this action because it's a POST method
          path: ['email-oauth', 'exchange-token'],
          data: a.body,
          command: 'getOauthAccessToken',
          module: 'entities',
          topic: 'cxengage/entities/get-oauth-access-token',
        })
      )
        .map((response) => handleSuccess(response, {
          ...a,
          type: 'FETCH_OAUTH_ACCESS_TOKEN_RESPONSE'
        }, 'OAuth Access Token acquired successfully'))
        .catch((error) => {
          let errorMessage = '';
          if (error.data.apiResponse && error.data.apiResponse.response) {
            errorMessage = error.data.apiResponse.response.error;
          }
          return handleError(error, {
            ...a,
            type: 'FETCH_OAUTH_ACCESS_TOKEN_RESPONSE'
          }, 'Unable to fetch Oauth Access Token. ' + errorMessage);
        })
    );

// Code related to MsTeams integration for Sync Users
export const SyncAllUsers = (action$, store) =>
  action$
    .ofType('SYNC_USERS_ENTITY')
    .map((a) => ({
      ...a,
      entityName: getCurrentEntity(store.getState()),
      selectedEntityId: getSelectedEntityId(store.getState()),
    }))
    .filter(
      ({ entityName }) =>
        entityName === 'integrations'
    )
    .map((a) => {

      a.sdkCall = {
        command: "createMsteamsSyncUsers",
        data: {
          syncAction: "start-sync"
        },
        module: 'entities',
        topic: 'cxengage/entities/create-integration-response',
        crudAction: "create",
      };
      return { ...a };
    })
    .concatMap((a) =>
      fromPromise(sdkPromise(a.sdkCall))
        .map((response) =>
          handleSuccessSyncUser(
            response,
            a,
            response.result
          )
        )
        .catch((error) => handleError(error, a))
    );

// Code related to MsTeams integration for Sync Users
export const SyncAllUsersProgress = (action$, store) =>
  action$
    .ofType('SYNC_USERS_ENTITY_PROGRESS')
    .delay(2000)
    .map((a) => ({
      ...a,
      entityName: getCurrentEntity(store.getState()),
      selectedEntityId: getSelectedEntityId(store.getState()),
    }))
    .filter(
      ({ entityName }) => {
        let isSyncInerror = getMsteamsSyncUserDataError(store.getState());
        return (entityName === 'integrations' && isSyncInerror == undefined || isSyncInerror !== true)
      }
    )
    .map((a) => {
      a.sdkCall = {
        command: "createMsteamsSyncUsers",
        data: {
          syncAction: "get-sync-status"
        },
        module: 'entities',
        topic: 'cxengage/entities/create-integration-response',
        crudAction: "create",
      };
      return { ...a };
    })
    .concatMap((a) =>
      fromPromise(sdkPromise(a.sdkCall))
        .map((response) =>
          handleProgressSuccess(
            response,
            a,
            response.result
          )
        )
        .catch((error) => handleError(error, a))
    );

let salesforceOauthPopup = null;

export const SalesforceOauthStart = (action$) =>
  action$
    .ofType('SF_OAUTH_START')
    .filter(({ integrationId }) => !!integrationId)
    .map((a) => {
      a.sdkCall = {
        module: 'entities',
        command: 'startSalesforceOauth',
        data: {
          integrationId: a.integrationId,
          ...(a.loginUrl ? { loginUrl: a.loginUrl } : {}),
          ...(a.tenantId ? { tenantId: a.tenantId } : {}),
        },
        topic: 'cxengage/entities/start-salesforce-oauth-response',
        crudAction: 'create',
      };
      return { ...a };
    })
    .concatMap((a) =>
      fromPromise(sdkPromise(a.sdkCall))
        .map((response) => {
          const result = response && response.result ? response.result : {};
          const authorizeUrl =
            result.authorizeUrl ||
            result.authorize_url ||
            result.redirectUrl ||
            result.redirect_url ||
            result.url;

          if (!authorizeUrl) {
            throw new Error('Missing authorize URL in Salesforce OAuth start response.');
          }

          if (salesforceOauthPopup && !salesforceOauthPopup.closed) {
            salesforceOauthPopup.close();
          }

          salesforceOauthPopup = window.open(
            authorizeUrl,
            'sfOauthWindow',
            'width=600,height=700,resizable=yes,scrollbars=yes'
          );
          const popup = salesforceOauthPopup;
          if (!popup || popup.closed || typeof popup.closed === 'undefined') {
            throw new Error('Popup blocked. Please allow popups for this site and try again.');
          }
          return handleSuccess(response, a);
        })
        .catch((error) => handleError(error, a, 'Unable to start Salesforce OAuth.'))
    );

export const SalesforceOauthReturnSuccess = (action$) =>
  action$
    .ofType('SF_OAUTH_RETURN_SUCCESS')
    .filter(({ integrationId }) => !!integrationId)
    .map((a) => {
      if (salesforceOauthPopup && !salesforceOauthPopup.closed) {
        try {
          salesforceOauthPopup.close();
        } catch (e) {
          // Ignore close failures; popup may have been closed by the callback page.
        }
      }
      salesforceOauthPopup = null;

      a.sdkCall = {
        module: 'entities',
        command: 'refreshSalesforceConnectionState',
        data: {
          integrationId: a.integrationId,
        },
        topic: 'cxengage/entities/get-integration-response',
        crudAction: 'read',
      };
      return { ...a };
    })
    .concatMap((a) =>
      fromPromise(sdkPromise(a.sdkCall))
        .mergeMap((response) =>
          from([
            handleSuccess(response, a),
            {
              type: 'FETCH_DATA_ITEM',
              entityName: 'integrations',
              id: a.integrationId,
            },
          ])
        )
        .catch((error) => handleError(error, a, 'Unable to refresh Salesforce connection state.'))
    );
